import { call, put, select } from 'redux-saga/effects';
import { imageDataSet } from '../../../actions/imageData';
import { scanStarted, scanCompleted, setCardId, setFormData } from '../../../actions/addInsurance';
import { putToS3, s3SignedUrl, getImageUploadFilename } from '../../../core/dapi/cardUpload';
import { analyticsTrackEvent } from '../../../core/analytics';
import { INSURANCE_TOUCH_NEXT_INSURANCE_COMPLETED } from '../../../core/analytics/events';
import { insuranceFormSubmission } from '../../../actions/insurance';
import { IMAGE_TYPE_BACK } from '../../../constants';
import { createInsuranceProfileUrl } from '../../../core/dapi/insuranceProfile';
import { apiPost, apiGet, apiPatch } from '../../../core/dapi';
import { getInsurersListUrl } from '../../../core/dapi/insurers';
import { insurersReceived, insurersError } from '../../../actions/insurers';
import { receiveInsuranceProfile } from '../../../actions/insuranceProfile';
import { buildInsuranceProfilePostData, buildNewBookingInsuranceData } from '../util/insuranceForm';
import { ADD_INSURANCE_ORIGIN_USER_PROFILE } from '../util/routing';
import { getUserProfileById } from '../../../core/dapi/userProfile';
import { receiveUserProfileForAccount } from '../../../actions/account';
import { insuranceDetailsSubmitting } from '../../../ducks/insurance/details';
import { safeGet } from '../../../core/util/object';
import { FILE_SIZE_THRESHOLD, JPEG_QUALITY } from '../../../routes/addInsurance';
import ExifImageLoader, { DEFAULT_JPEG_QUALITY } from '../../../core/util/ExifImageLoader';
import { createBlob } from '../../../core/util/image';

/**
 * Steps:
 *
 * If adding insurance from account profile:
 * 1. Create Insurance Profile
 * 2. Patch user profile with insurance_profile_id
 * 3. Insurance coverage / PokitDok check
 * (send userProfileId to automatically do user profile patch with insurance profile)
 *
 * If adding insurance during booking (booking widget or app booking):
 * 1. Insurance coverage / PokitDock check
 * No need to do anything else here, since submitting the booking later
 * will take care of insurance profile and user profile updates
 */
export function* submitInsuranceForm({ flow, form, location, currentUser }) {
  analyticsTrackEvent(INSURANCE_TOUCH_NEXT_INSURANCE_COMPLETED);
  const getMarketplaceInsurersFromState = state => {
    const safeState = safeGet(state);
    return safeState('marketplaceInsurers.insurers') || [];
  };

  const marketplaceInsurers = yield select(getMarketplaceInsurersFromState);
  const newBookingInsuranceData = buildNewBookingInsuranceData(
    form,
    marketplaceInsurers,
    currentUser.userProfileId,
    currentUser.accountId
  );
  yield put(insuranceFormSubmission(newBookingInsuranceData));
  yield put(setFormData(form));

  let newInsuranceProfileId = null;

  if (flow === ADD_INSURANCE_ORIGIN_USER_PROFILE) {
    const accountId = currentUser.accountId;
    const userProfileId = currentUser.userProfileId;
    yield put(insuranceDetailsSubmitting(true));

    const insuranceProfileUrl = createInsuranceProfileUrl();
    const userProfilePatchUrl = getUserProfileById(userProfileId);

    const insuranceProfilePostData = buildInsuranceProfilePostData(
      form,
      accountId,
      marketplaceInsurers
    );

    const createInsuranceProfileResponse = yield call(
      apiPost,
      insuranceProfileUrl,
      insuranceProfilePostData
    );
    yield put(
      receiveInsuranceProfile({
        ...createInsuranceProfileResponse,
        insurance_profile_id: createInsuranceProfileResponse.id,
      })
    );

    newInsuranceProfileId = createInsuranceProfileResponse.id;

    const userProfilePatchData = {
      dental_insurance_profile_id: newInsuranceProfileId,
    };
    const userProfilePatchResponse = yield call(
      apiPatch,
      userProfilePatchUrl,
      userProfilePatchData
    );
    yield put(receiveUserProfileForAccount(userProfilePatchResponse));
  }
  yield put(insuranceDetailsSubmitting(false));
}

export function* uploadImage({ imgType, imageFile }) {
  try {
    yield put(scanStarted(imgType));

    const jpegQuality = imageFile.size > FILE_SIZE_THRESHOLD ? JPEG_QUALITY : DEFAULT_JPEG_QUALITY;

    const imageLoader = new ExifImageLoader(imageFile);
    const rawJpeg = yield call([imageLoader, 'getOrientedImage'], jpegQuality);

    yield put(imageDataSet(rawJpeg, imgType));

    const fileBlob = createBlob(rawJpeg);
    const data = {
      image_type: imgType === IMAGE_TYPE_BACK ? 'back' : 'front',
      ocr_response: {},
      insurance_profile_id: null,
    };

    const { aws_path: name, id: cardId } = yield call(apiPost, getImageUploadFilename(), data);
    yield put(setCardId(cardId, imgType));

    const image = { name, image: fileBlob, type: 'image/jpg' };
    const { signedRequest } = yield call(s3SignedUrl, image);

    yield call(putToS3, signedRequest, image);
    yield put(scanCompleted(imgType));
  } catch (e) {
    console.error(e);
  }
}
